import { useEffect, useState } from "react";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import messages from "../../../constants/messages";
import useAuthHeader from "../../../hooks/useAuthHeader";
import { useRuleWizardContext } from "../../../providers/RuleWizardProvider";
import CoAppTextField from "../../global/components/inputs/CoAppTextField";
import TagInput from "../../tags/TagInput";

export default function WizardStep1() {
	let authHeader = useAuthHeader();

	const { 
		name, description, tags, 
		setName, setDescription, setTags, 
		editedId, setStepDataHasChanged,
		stepOneErrors, setStepOneErrors, editedVersionOfRule, platformOptions, setPlatformId, platformId, handleSaveChanges
	} = useRuleWizardContext();

	const [tagsInCurrentOrganization, setTagsInCurrentOrganization] = useState([]);
	const [hasLoaded, setHasLoaded] = useState(false);

	const initExistingTags = () => {
		if (!hasLoaded) {
			axios.get(apiRoutes.getTags, {
				headers: authHeader
			}).then(res => {
				setTagsInCurrentOrganization(res.data);
			}).catch(err => {
				console.error(err);
			});
		}
		setHasLoaded(true);
	};

	const handleFieldChange = (field, setFieldAction, maxLength) => (e) => {
		let value = e.target.value;
		if (value.length > maxLength) {
			setStepOneErrors({
				...stepOneErrors,
				[`${field}Error`]: true,
				[`${field}ErrorMessage`]: messages.TOO_MANY_CHARACTERS_ERROR_MSG(maxLength, value.length - maxLength)
			});
			setFieldAction(value);
		} else {
			delete stepOneErrors[`${field}Error`];
			delete stepOneErrors[`${field}ErrorMessage`];
			setStepOneErrors({ ...stepOneErrors });
			setFieldAction(value);
		}
	};

	const setCreateTags = (tags) => {
		setTags(tags);

		// only save if we are editing an existing rule
		if (editedId) {
			handleSaveChanges(null, tags);
		}
	};

	useEffect(() => {
		initExistingTags();
		if (editedId && Object.keys(editedVersionOfRule).length > 0) {
			if (name !== editedVersionOfRule.name || description !== editedVersionOfRule.description ||
				tags.length !== editedVersionOfRule.tags.length) {
				setStepDataHasChanged(true);
			}
		}
	}, [description, name, tags]);

	return (
		<Stack spacing={2} sx={{ marginTop: "150px" }}>
			<Typography variant="h6">Name, describe, and tag the rule</Typography>
			<CoAppTextField
				error={stepOneErrors.nameError}
				fullWidth={false}
				helperText={stepOneErrors.nameErrorMessage ? stepOneErrors.nameErrorMessage : `${name.length}/50`}
				label="Name"
				name="ruleName"
				onChange={handleFieldChange("name", setName, 50)}
				placeholder="Name"
				size="medium"
				sx={{ width: "60%" }}
				type="text"
				value={name}
			/>

			<CoAppTextField
				error={stepOneErrors.descriptionError}
				fullWidth={false}
				helperText={stepOneErrors.descriptionErrorMessage ? stepOneErrors.descriptionErrorMessage : `${description.length}/255`}
				label="Description"
				name="description"
				onChange={handleFieldChange("description", setDescription, 255)}
				placeholder="Description"
				size="medium"
				sx={{ width: "60%" }}
				type="text"
				value={description}
			/>

			<TagInput createTags={setCreateTags} disabled={description.length < 1 || name.length < 1} textFieldWidth={ "60%" } tags={tags} tagsInCurrentOrg={tagsInCurrentOrganization} />
		
			{
				platformOptions.length > 1 && (
					<FormControl sx={{ width: "60%" }}>
						<InputLabel id="platform-label">Platform</InputLabel>
						<Select
							labelId="platform-label"
							label="Platform"
							name="platform"
							onChange={handleFieldChange("platformId", setPlatformId)}
							value={platformId}
							sx={{ width: "60%" }}
						>
							{platformOptions.map(option => (
								<MenuItem key={option.id} value={option.id}>
									{option.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText error={stepOneErrors.platformError} sx={{ marginTop: "5px" }}>{stepOneErrors.platformErrorMessage ? stepOneErrors.platformErrorMessage : null}</FormHelperText>
					</FormControl>
				)
			}
		</Stack>
	);
}